import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { ChainSelector } from '../ui/ChainSelector';
import { TokenSelector } from '../ui/TokenSelector';
import { useBridge } from '../../hooks/useBridge';
import { useWallet } from '../../hooks/useWallet';
import { CHAIN_CONFIGS } from '../../utils/constants';
// import { switchNetwork } from '../../utils/web3';

export const BridgeForm: React.FC = () => {
  const { walletInfo, changeNetwork } = useWallet();
  const { 
    fromChain,
    toChain,
    selectedToken,
    amount,
    recipient,
    balance,
    fee,
    estimatedReceiveAmount,
    bridgeFee,
    feeBreakdown,
    transactionState,
    handleFromChainChange,
    handleToChainChange,
    handleTokenChange,
    handleAmountChange,
    handleRecipientChange,
    handleApproveToken,
    handleBridgeToken,
    needsApproval
  } = useBridge(walletInfo.address);

  const [networkError, setNetworkError] = useState<string | null>(null);

  // Check if wallet is connected to the correct network
  useEffect(() => {
    if (!walletInfo.isConnected || !fromChain) {
      setNetworkError(null);
      return;
    }

    const targetChainId = CHAIN_CONFIGS[fromChain]?.chainId;
    if (targetChainId && targetChainId !== walletInfo.chainId) {
      setNetworkError(`Please switch to ${CHAIN_CONFIGS[fromChain].name}`);
    } else {
      setNetworkError(null);
    }
  }, [walletInfo.isConnected, walletInfo.chainId, fromChain]);

  // Handle network switch
  const handleNetworkSwitch = async () => {
    if (!fromChain) return;
    
    const targetChainId = CHAIN_CONFIGS[fromChain]?.chainId;
    if (!targetChainId) return;
    
    try {
      await changeNetwork(targetChainId);
      setNetworkError(null);
    } catch (error) {
      console.error('Failed to switch network:', error);
    }
  };

  // Determine if the form is valid
  const isFormValid = () => {
    return (
      walletInfo.isConnected &&
      fromChain &&
      toChain &&
      selectedToken &&
      amount &&
      parseFloat(amount) > 0 &&
      recipient &&
      ethers.utils.isAddress(recipient) &&
      !networkError
    );
  };

  // Determine if the token is available on the selected chain
  const isTokenAvailable = () => {
    if (!fromChain || !selectedToken) return true;
    return !!CHAIN_CONFIGS[fromChain][selectedToken];
  };

  // Get available chains for "to" selector based on "from" selection
  const getToChainOptions = () => {
    if (!fromChain) return [];
    
    if (fromChain === 'shido-mainnet') {
      // If from chain is Shido, to chain can be any other chain
      return Object.keys(CHAIN_CONFIGS).filter(key => key !== 'shido-mainnet');
    } else {
      // If from chain is not Shido, to chain can only be Shido
      return ['shido-mainnet'];
    }
  };

  return (
    <Card 
      title="Bridge Tokens" 
      subtitle="Transfer tokens between chains securely"
      elevated
    >
      <div className="space-y-6">
        {/* Network Error Alert */}
        {networkError && (
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 flex items-start">
            <div className="text-red-500 mr-3 flex-shrink-0">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm text-red-400">{networkError}</p>
              <Button 
                variant="danger" 
                size="sm" 
                className="mt-2" 
                onClick={handleNetworkSwitch}
              >
                Switch Network
              </Button>
            </div>
          </div>
        )}

        {/* Chain Selection */}
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ChainSelector
              label="From Chain"
              value={fromChain}
              onChange={handleFromChainChange}
              disabled={transactionState.isLoading}
            />
            
            <ChainSelector
              label="To Chain"
              value={toChain}
              onChange={handleToChainChange}
              disabled={!fromChain || transactionState.isLoading}
              excludeChains={fromChain ? [fromChain] : []}
              onlyChains={getToChainOptions()}
            />
          </div>
          
          {/* Chain Swap Button */}
          {fromChain && toChain && (
            <button
              type="button"
              onClick={() => {
                if (fromChain === 'shido-mainnet' || toChain === 'shido-mainnet') {
                  const newFromChain = toChain;
                  handleFromChainChange(newFromChain);
                }
              }}
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gray-700 hover:bg-gray-600 text-primary-400 p-2 rounded-full shadow-lg hidden md:flex items-center justify-center z-10"
              disabled={transactionState.isLoading || !(fromChain === 'shido-mainnet' || toChain === 'shido-mainnet')}
              title="Swap chains"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
              </svg>
            </button>
          )}
        </div>

        {/* Token Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Select Token
          </label>
          <TokenSelector
            selectedToken={selectedToken}
            onChange={handleTokenChange}
            disabled={transactionState.isLoading}
          />
          {!isTokenAvailable() && (
            <p className="mt-1 text-sm text-red-500">
              {selectedToken} is not available on {CHAIN_CONFIGS[fromChain]?.name}
            </p>
          )}
        </div>

        {/* Amount Input with Balance */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-300">Amount</label>
            {balance && parseFloat(balance) > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-400">Balance: {parseFloat(balance).toFixed(2)} {selectedToken}</span>
                <button 
                  type="button"
                  onClick={() => {
                    // Limit balance to 2 decimal places when using Max button
                    let maxAmount = balance;
                    if (maxAmount && maxAmount.includes('.')) {
                      const parts = maxAmount.split('.');
                      if (parts[1] && parts[1].length > 2) {
                        maxAmount = parts[0] + '.' + parts[1].substring(0, 2);
                      }
                    }
                    handleAmountChange(maxAmount);
                  }}
                  className="text-xs text-primary-400 hover:text-primary-300"
                >
                  Max
                </button>
              </div>
            )}
          </div>
          <Input
            type="number"
            value={amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            placeholder="0.0"
            min="0"
            step="0.000001"
            disabled={transactionState.isLoading || !isTokenAvailable()}
            fullWidth
          />
        </div>

        {/* Estimated Receive Amount */}
        {amount && parseFloat(amount) > 0 && bridgeFee && parseFloat(bridgeFee) > 0 && (
          <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-blue-300">Total Bridge Fee:</span>
              <span className="text-sm text-blue-200">{bridgeFee} {selectedToken}</span>
            </div>
            
            {/* Fee Breakdown */}
            {feeBreakdown && (
              <div className="mt-2 pt-2 border-t border-blue-800/50 space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-blue-400">• Transport Fee:</span>
                  <span className="text-xs text-blue-300">{feeBreakdown.transportFee} {selectedToken}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-blue-400">• Service Fee ({feeBreakdown.serviceFeePercentage}%):</span>
                  <span className="text-xs text-blue-300">{feeBreakdown.serviceFee} {selectedToken}</span>
                </div>
              </div>
            )}
            
            <div className="flex justify-between items-center mt-2 pt-2 border-t border-blue-800/50">
              <span className="text-sm font-medium text-blue-300">You will receive (estimate):</span>
              <span className="text-sm font-medium text-blue-100">
                {estimatedReceiveAmount} {selectedToken}
              </span>
            </div>
            <p className="text-xs text-blue-400 mt-1">
              Estimated amounts - actual fees may vary slightly
            </p>
            <p className="text-xs text-blue-400 mt-1">
              Estimated time: 2-10 minutes for cross-chain transfer
            </p>
          </div>
        )}

        {/* Recipient Address */}
        <Input
          label="Recipient Address"
          type="text"
          value={recipient}
          onChange={(e) => handleRecipientChange(e.target.value)}
          placeholder="0x..."
          disabled={transactionState.isLoading}
          fullWidth
        />


        {/* Transaction Status */}
        {transactionState.error && (
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-400">{transactionState.error}</p>
          </div>
        )}

        {transactionState.success && transactionState.hash && (
          <div className="bg-green-900/30 border border-green-800 rounded-lg p-3">
            <p className="text-sm text-green-400">Transaction successful!</p>
            <a 
              href={`${CHAIN_CONFIGS[fromChain]?.explorerUrl}/tx/${transactionState.hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary-400 hover:text-primary-300 underline mt-1 inline-block"
            >
              View on Explorer
            </a>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {needsApproval() && (
            <Button
              onClick={handleApproveToken}
              disabled={!isFormValid() || transactionState.isLoading || !isTokenAvailable()}
              isLoading={transactionState.isLoading}
              fullWidth
              variant="secondary"
            >
              Approve {selectedToken}
            </Button>
          )}
          
          <Button
            onClick={handleBridgeToken}
            disabled={!isFormValid() || transactionState.isLoading || needsApproval() || !isTokenAvailable()}
            isLoading={transactionState.isLoading}
            fullWidth
          >
            Bridge Tokens
          </Button>
        </div>
      </div>
    </Card>
  );
};
